/**
 * Loan Completion Utilities
 * Helper functions to check if loan accounts have required details
 */

/**
 * Check if an account is a loan type
 */
export const isLoanAccount = (account) => {
  const loanTypes = ['loan', 'mortgage'];
  const accountType = account.accountType?.toLowerCase() || account.type?.toLowerCase() || '';
  const accountSubtype = account.subtype?.toLowerCase() || '';
  
  return loanTypes.includes(accountType) || loanTypes.includes(accountSubtype);
};

/**
 * Get missing required fields for a loan account
 */
export const getMissingLoanFields = (account) => {
  if (!isLoanAccount(account)) {
    return [];
  }
  
  const missing = [];
  
  // Check Asset Category (REQUIRED)
  const assetCategory = account.loanDetails?.assetCategory || account.assetCategory;
  if (!assetCategory) {
    missing.push({
      field: 'assetCategory',
      label: 'Asset Category',
      description: 'What type of asset is this loan for? (e.g., Auto, Mortgage, Personal)'
    });
  }
  
  // Check Ownership Type (REQUIRED)
  const ownershipType = account.loanDetails?.ownershipType || account.loanOwnershipType;
  if (!ownershipType) {
    missing.push({
      field: 'ownershipType',
      label: 'Ownership Type',
      description: 'How do you own this asset? (Financed, Lease, or Owned Outright)'
    });
  }
  
  return missing;
};

/**
 * Check if loan account is complete
 */
export const isLoanComplete = (account) => {
  // Backend might have this field already
  if (account.isLoanComplete !== undefined) {
    return account.isLoanComplete;
  }
  
  // Otherwise check client-side
  if (!isLoanAccount(account)) {
    return true;
  }
  
  return getMissingLoanFields(account).length === 0;
};

/**
 * Get all incomplete loan accounts from a list
 */
export const getIncompleteLoans = (accounts) => {
  if (!Array.isArray(accounts)) return [];
  
  return accounts.filter(account => {
    return isLoanAccount(account) && !isLoanComplete(account);
  });
};

/**
 * Get user-friendly field labels
 */
export const getFieldLabel = (fieldName) => {
  const labels = {
    assetCategory: 'Asset Category',
    ownershipType: 'Ownership Type',
    originalAmount: 'Original Loan Amount',
    currentValue: 'Current Asset Value'
  };
  
  return labels[fieldName] || fieldName;
};

/**
 * Get asset category options
 */
export const getAssetCategoryOptions = () => [
  { value: 'auto', label: '🚗 Auto Loan', description: 'Car, truck, or vehicle loan' },
  { value: 'mortgage', label: '🏠 Mortgage', description: 'Home or real estate loan' },
  { value: 'personal', label: '💳 Personal Loan', description: 'Unsecured personal loan' },
  { value: 'student', label: '🎓 Student Loan', description: 'Education loan' },
  { value: 'business', label: '💼 Business Loan', description: 'Business or commercial loan' },
  { value: 'other', label: '📋 Other', description: 'Other type of loan' }
];

/**
 * Get ownership type options
 */
export const getOwnershipTypeOptions = () => [
  { 
    value: 'financed', 
    label: '💰 Financed', 
    description: 'You own the asset but are paying off a loan',
    impact: 'Asset value - Loan balance = Your equity'
  },
  { 
    value: 'lease', 
    label: '📝 Lease', 
    description: "You don't own the asset, making lease payments",
    impact: 'Only lease payments count as debt (no asset value)'
  },
  { 
    value: 'owned', 
    label: '✅ Owned Outright', 
    description: 'You own the asset completely, no loan',
    impact: 'Full asset value added to net worth (no debt)'
  }
];

